import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function StudentProfile() {
  const { studentId } = useParams();

  // Data
  const [profile, setProfile] = useState(null);
  const [rows, setRows] = useState([]); // progress rows
  const [classrooms, setClassrooms] = useState([]); // classroom id->name map

  // UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters / search / sort
  const [q, setQ] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [classroomFilter, setClassroomFilter] = useState('all');
  const [sortKey, setSortKey] = useState('updated'); // 'updated' | 'title' | 'score'
  const [sortDir, setSortDir] = useState('desc');    // 'asc' | 'desc'

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        // Profile
        const { data: prof, error: pErr } = await supabase
          .from('profiles')
          .select('id, username, role')
          .eq('id', studentId)
          .maybeSingle();
        if (pErr || !prof) throw pErr || new Error('Student not found');
        if (!mounted) return;
        setProfile(prof);

        // Progress with lesson join (title, level)
        const { data: progress, error: progErr } = await supabase
          .from('classroom_lesson_progress')
          .select('classroom_id, lesson_id, score, writing_response, updated_at, lesson:lessons(title, level)')
          .eq('student_id', studentId);
        if (progErr) throw progErr;

        // Fetch classroom names for badging
        const classroomIds = [...new Set((progress || []).map(r => r.classroom_id).filter(Boolean))];
        let clsMap = {};
        if (classroomIds.length > 0) {
          const { data: cls, error: cErr } = await supabase
            .from('classrooms')
            .select('id, name')
            .in('id', classroomIds);
          if (cErr) throw cErr;
          clsMap = Object.fromEntries(cls.map(c => [c.id, c.name]));
        }

        if (!mounted) return;
        setRows(progress || []);
        setClassrooms(clsMap);
      } catch (e) {
        console.error(e);
        if (mounted) setError('Could not load student progress.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [studentId]);

  const filtered = useMemo(() => {
    let out = [...rows];

    if (q.trim()) {
      const needle = q.toLowerCase();
      out = out.filter(r =>
        (r.lesson?.title || '').toLowerCase().includes(needle) ||
        (r.writing_response || '').toLowerCase().includes(needle)
      );
    }

    if (levelFilter !== 'all') {
      out = out.filter(r => (r.lesson?.level || 'beginner') === levelFilter);
    }

    if (classroomFilter !== 'all') {
      out = out.filter(r => r.classroom_id === classroomFilter);
    }

    // sort
    out.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'title') {
        const A = (a.lesson?.title || '').toLowerCase();
        const B = (b.lesson?.title || '').toLowerCase();
        return A < B ? -1 * dir : A > B ? 1 * dir : 0;
        }
      if (sortKey === 'score') {
        const A = typeof a.score === 'number' ? a.score : -Infinity;
        const B = typeof b.score === 'number' ? b.score : -Infinity;
        return A < B ? -1 * dir : A > B ? 1 * dir : 0;
      }
      // default: updated
      const A = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const B = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return A < B ? -1 * dir : A > B ? 1 * dir : 0;
    });

    return out;
  }, [rows, q, levelFilter, classroomFilter, sortKey, sortDir]);

  const levels = useMemo(() => {
    const set = new Set(rows.map(r => r.lesson?.level || 'beginner'));
    return ['beginner', 'intermediate', 'advanced'].filter(l => set.has(l));
  }, [rows]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="h-8 w-1/2 bg-gray-100 rounded animate-pulse" />
        <div className="h-24 w-full bg-gray-100 rounded animate-pulse" />
        <div className="h-72 w-full bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="rounded border border-red-200 bg-red-50 p-3 text-red-700">{error || 'Student not found.'}</div>
      </div>
    );
  }

  const total = rows.length;
  const completed = rows.filter(r => r.score != null || r.writing_response).length;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{profile.username}</h1>
          <p className="text-sm text-gray-600">Role: {profile.role}</p>
          <p className="text-sm text-gray-600 mt-1">
            Lessons Touched: <span className="font-medium">{total}</span> · Completed: <span className="font-medium">{completed}</span>
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 grid md:grid-cols-4 gap-3">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title or response…"
          className="w-full p-2 border border-gray-300 rounded"
        />

        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
        >
          <option value="all">All levels</option>
          {levels.map(l => <option key={l} value={l}>{l[0].toUpperCase() + l.slice(1)}</option>)}
        </select>

        <select
          value={classroomFilter}
          onChange={(e) => setClassroomFilter(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
        >
          <option value="all">All classrooms</option>
          {Object.entries(classrooms).map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>

        <div className="flex gap-2">
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded"
          >
            <option value="updated">Sort by: Recently updated</option>
            <option value="title">Sort by: Title</option>
            <option value="score">Sort by: Score</option>
          </select>
          <button
            onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
            className="px-3 py-2 border border-gray-300 rounded"
            title="Toggle sort direction"
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-600">
            No matching lessons.
          </div>
        ) : (
          filtered.map((entry, idx) => {
            const clsName = entry.classroom_id ? classrooms[entry.classroom_id] : null;
            const level = entry.lesson?.level || 'beginner';
            const title = entry.lesson?.title || 'Untitled lesson';
            const hasWriting = !!entry.writing_response;
            const hasScore = typeof entry.score === 'number';

            return (
              <div
                key={`${entry.lesson_id}-${idx}`}
                className="bg-white border border-gray-200 rounded-xl shadow-sm p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
                    <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-full border px-2 py-0.5">
                        Level: {level}
                      </span>
                      {clsName && (
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5">
                          Class: {clsName}
                        </span>
                      )}
                      {entry.updated_at && (
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5">
                          Updated: {new Date(entry.updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="text-sm">
                      {hasScore ? (
                        <span className="font-medium text-blue-700">Score: {entry.score}</span>
                      ) : (
                        <span className="text-gray-500">Not graded</span>
                      )}
                    </div>
                  </div>
                </div>

                {hasWriting && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-600 mb-1">Writing Response</p>
                    <p className="text-gray-800 whitespace-pre-wrap border rounded p-3 bg-gray-50">
                      {entry.writing_response}
                    </p>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {/* Link for teacher to open grading view, if in a classroom */}
                  {entry.classroom_id && (
                    <Link
                      to={`/feedback/${entry.classroom_id}/${entry.lesson_id}/${studentId}`}
                      className="px-3 py-1.5 rounded bg-purple-600 text-white text-sm hover:bg-purple-500"
                    >
                      Review & Grade
                    </Link>
                  )}
                  {/* Quick open lesson (student path) if you want */}
                  <Link
                    to={`/modules/${level}/${entry.lesson_id}${entry.classroom_id ? `?classroomId=${entry.classroom_id}` : ''}`}
                    className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50"
                  >
                    Open Lesson
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
